import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

type ApiEnvelope = {
  error?: {
    message?: unknown;
    reasonCode?: unknown;
    family?: unknown;
    [key: string]: unknown;
  };
  message?: unknown;
  [key: string]: unknown;
};

function normalizeToken(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const token = value.trim();
  return token ? token : null;
}

function withSupportCode(message: string, reasonCode: string, family?: string | null): string {
  const suffix = family
    ? `Support code: ${reasonCode} (${family})`
    : `Support code: ${reasonCode}`;
  if (message.includes('Support code:')) return message;
  return `${message} (${suffix})`;
}

function patchErrorEnvelope(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const body = payload as ApiEnvelope;
  const nested = body.error && typeof body.error === 'object'
    ? body.error
    : null;

  const reasonCode = normalizeToken(nested?.reasonCode);
  if (!reasonCode) {
    return payload;
  }

  const family = normalizeToken(nested?.family);
  const nestedMessage = normalizeToken(nested?.message);
  if (nested && nestedMessage) {
    return {
      ...body,
      error: {
        ...nested,
        message: withSupportCode(nestedMessage, reasonCode, family),
      },
    };
  }

  const rootMessage = normalizeToken(body.message);
  if (rootMessage) {
    return {
      ...body,
      message: withSupportCode(rootMessage, reasonCode, family),
    };
  }

  return payload;
}

@Injectable()
export class ErrorSupportCodeInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const patchedPayload = patchErrorEnvelope(error?.error);
        if (patchedPayload === error?.error) {
          return throwError(() => error);
        }
        const patched = new HttpErrorResponse({
          error: patchedPayload,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url || undefined,
        });
        return throwError(() => patched);
      })
    );
  }
}
